import { appendUrlParams } from "utils/urlUtils";
import { getS3AssetCDNUrl } from "features/Common/modules/File/modules/Upload/utils/FileUpload.utils";
import { OFFERING_STATUSES } from "constants/schedule";
import { thank_you_pages_apis } from "./ThankYouPages.api";

export const THANK_YOU_PAGES_ROUTE_KEYS = {
  thank_you_pages_home: "thank_you_pages_home",
  thank_you_pages_create: "thank_you_pages_create",
  thank_you_pages_update_template: "thank_you_pages_update_template",
  thank_you_pages_update_details: "thank_you_pages_update_details",
  thank_you_pages_preview: "thank_you_pages_preview",
  thank_you_page_template_preview: "thank_you_page_template_preview",
};

export const THANK_YOU_PAGES_ROUTE_IDS = {
  [THANK_YOU_PAGES_ROUTE_KEYS.thank_you_pages_home]: "thank-you-pages",
  [THANK_YOU_PAGES_ROUTE_KEYS.thank_you_pages_create]: "thank-you-pages/create",
  [THANK_YOU_PAGES_ROUTE_KEYS.thank_you_pages_update_template]:
    "thank-you-pages/update/:thankYouPageUuid/template",
  [THANK_YOU_PAGES_ROUTE_KEYS.thank_you_pages_update_details]:
    "/thank-you-pages/update/details",
  [THANK_YOU_PAGES_ROUTE_KEYS.thank_you_pages_preview]:
    "thank-you-pages/preview/:thankYouPageUuid",
  [THANK_YOU_PAGES_ROUTE_KEYS.thank_you_page_template_preview]:
    "thank-you-pages/template/preview/:templateUuid",
};

export const THANK_YOU_PAGES_ROUTE_GENERATORS = {
  [THANK_YOU_PAGES_ROUTE_KEYS.thank_you_pages_home]: ({
    utmParams = {},
  } = {}) => {
    const basePath = `/${
      THANK_YOU_PAGES_ROUTE_IDS[THANK_YOU_PAGES_ROUTE_KEYS.thank_you_pages_home]
    }`;
    const utmString = new URLSearchParams(utmParams).toString();
    return utmString ? `${basePath}?${utmString}` : basePath;
  },
  [THANK_YOU_PAGES_ROUTE_KEYS.thank_you_pages_update_template]: ({
    thankYouPageUuid,
  }) => `thank-you-pages/update/${thankYouPageUuid}/template`,
  [THANK_YOU_PAGES_ROUTE_KEYS.thank_you_pages_update_details]: ({
    thankYouPageUuid,
    templateUuid,
  }) =>
    appendUrlParams(`/thank-you-pages/update/details`, {
      templateUuid,
      thankYouPageUuid,
    }),
  [THANK_YOU_PAGES_ROUTE_KEYS.thank_you_pages_preview]: ({
    thankYouPageUuid,
  }) => `thank-you-pages/preview/${thankYouPageUuid}`,
  [THANK_YOU_PAGES_ROUTE_KEYS.thank_you_page_template_preview]: ({
    templateUuid,
  }) => `/thank-you-pages/template/preview/${templateUuid}`,
};

export const THANK_YOU_PAGES_RESOURCE_APIS = {
  [THANK_YOU_PAGES_ROUTE_IDS[THANK_YOU_PAGES_ROUTE_KEYS.thank_you_pages_home]]:
    ({ params }) => {
      const queries = { ...params?.filter };
      queries[thank_you_page_home_stage_filter_key] =
        queries[thank_you_page_home_stage_filter_key] ??
        default_thank_you_page_stage_id;
      if (queries.utm_source) delete queries.utm_source;
      return {
        api_end_point: thank_you_pages_apis.get_thank_you_pages_list,
        queries,
      };
    },
};

export const thank_you_page_detail_keys = {
  uuid: "uuid",
  title: "title",
  thank_you_page_stage: "thank_you_page_stage",
  listing_title: "listing_title",
  listing_uuid: "listing_uuid",
  redirect_url: "redirect_url",
  thank_you_page_url: "thankyou_page_url",
  page_builder_source: "page_builder_source",
  template_uid: "template_uid",
  url_slug: "url_slug",
  metadata: "metadata",
  seo_details: "seo_details",
  page_source: "page_source",
};

export const thank_you_page_metadata_keys = {
  cdn_url: "cdn_url",
  project_data_url: "project_data_url",
  cdn_draft_url: "cdn_draft_url",
  project_data_draft_url: "project_data_draft_url",
  page_builder_source: "page_builder_source",
  html_redirect_url: "html_redirect_url",
  theme: "theme",
  custom_ga_token: "custom_ga_token",
  custom_pixel_token: "custom_pixel_token",
};

export const thank_you_page_home_stage_filter_key =
  thank_you_page_detail_keys.thank_you_page_stage;

export const UTM_SRC_URL_PARAMS_KEY = "utm_source";

export const PAGE_BUILDER_SOURCES = {
  exly: 1,
  grapes_js: 2,
  ai_builder: 3,
};

export const THANK_YOU_SALE_PAGE_SECTION_SLUGS = {
  upsell: "Upsell",
};

export const thank_you_builder_types = {
  classic: 1,
  advanced: 2,
};

export const thank_you_builder_options = {
  [thank_you_builder_types.classic]: {
    id: 1,
    heading: "Classic Editor",
    description: "Easy setup—ideal for creating pages with minimal tweaks",
    value: thank_you_builder_types.classic,
    url: getS3AssetCDNUrl("assets/images/sales_pages/classic.png"),
  },
  [thank_you_builder_types.advanced]: {
    id: 2,
    heading: "Advanced Builder",
    description:
      "Built for experts: Full design freedom with unmatched customization",
    value: thank_you_builder_types.advanced,
    url: getS3AssetCDNUrl("assets/images/sales_pages/advanced.png"),
  },
};

export const thank_you_page_template_tabs = {
  all: "all",
  popular: "popular",
};

export const default_thank_you_page_template_tab =
  thank_you_page_template_tabs.all;

export const THANK_YOU_PAGE_LINK_TYPES = {
  OFFERING: "offering",
  PAYMENT_LINK: "payment_link",
};

export const thank_you_page_listing_status = [
  OFFERING_STATUSES.live.value,
  OFFERING_STATUSES.soldout.value,
];

export const THANK_YOU_PAGE_GOALS = {
  PAYMENT_CONVERSION: "payment_conversion",
  LEAD_CAPTURE: "lead_capture",
};

export const PAGE_TYPE_DROPDOWN_LABEL = {
  label: "Page type",
  size: "small",
  weight: "semi-bold",
};

export const default_thank_you_page_stage_id = 3;
export const thank_you_page_stages = {
  template_selecting: 1,
  details_editing: 2,
  published: 3,
};

export const thank_you_page_template_tabs_options = [
  { label: "All templates", value: thank_you_page_template_tabs.all },
  { label: "Most popular", value: thank_you_page_template_tabs.popular },
];

export const THANK_YOU_TEMPLATE_SLUGS = {
  custom_grapesjs_scratch: "custom-grapesjs-scratch",
};

// Single source of truth for page-type constants so `thankyouS3Routes.js`
// and other utils can import from `ThankYouPages.constants` directly.
export const THANK_YOU_PAGE_TYPE_VALUES = {
  PURCHASE: 1,
  OPTIN: 2,
};

export const THANK_YOU_PAGE_TYPE_STRINGS = {
  PURCHASE: "purchase",
  OPTIN: "optin",
  OPT_IN: "opt_in",
};
